import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { currencyService } from '../services/currencyService';
import { useSiteSettings } from '../services/siteSettingsService';

const scheduleTimeSlots = [
  '09:00', '10:00', '11:00', '14:00', '15:00', '16:00'
];

const budgetRanges = [
  { value: '<5k', minEUR: 0, maxEUR: 5000, type: 'under' as const },
  { value: '5k-10k', minEUR: 5000, maxEUR: 10000, type: 'range' as const },
  { value: '10k-20k', minEUR: 10000, maxEUR: 20000, type: 'range' as const },
  { value: '20k+', minEUR: 20000, maxEUR: null, type: 'over' as const },
];

const getScheduleDateInBrowserTimezone = (date: string, time: string, timeZone: string) => {
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  const desiredUtcTime = Date.UTC(year, month - 1, day, hour, minute);
  const utcGuess = new Date(desiredUtcTime);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter
    .formatToParts(utcGuess)
    .reduce<Record<string, string>>((accumulator, part) => {
      if (part.type !== 'literal') {
        accumulator[part.type] = part.value;
      }

      return accumulator;
    }, {});
  const zonedUtcTime = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute)
  );

  return new Date(utcGuess.getTime() - (zonedUtcTime - desiredUtcTime));
};

const convertScheduleTimeToLocal = (time: string, date: string, timeZone: string) =>
  getScheduleDateInBrowserTimezone(date, time, timeZone).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

const BookCall = () => {
  const { settings } = useSiteSettings();
  const [country, setCountry] = useState(currencyService.getCountry());
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    description: ''
  });

  useEffect(() => {
    const unsubscribe = currencyService.subscribe((nextCountry) => {
      setCountry(nextCountry);
    });

    return unsubscribe;
  }, []);

  const currencyConfig = currencyService.getCurrencyConfig(country);

  const formatBudgetAmount = (amountEUR: number) => {
    const convertedAmount = currencyService.convertFromEUR(amountEUR);

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyConfig.code,
      maximumFractionDigits: 0,
    }).format(convertedAmount);
  };

  const budgetOptions = budgetRanges.map((range) => {
    if (range.type === 'under') {
      return {
        value: range.value,
        label: `Moins de ${formatBudgetAmount(range.maxEUR || 0)}`,
      };
    }

    if (range.type === 'over') {
      return {
        value: range.value,
        label: `Plus de ${formatBudgetAmount(range.minEUR)}`,
      };
    }

    return {
      value: range.value,
      label: `${formatBudgetAmount(range.minEUR)} - ${formatBudgetAmount(range.maxEUR || 0)}`,
    };
  });

  const selectedBudgetOption =
    budgetOptions.find((option) => option.value === formData.budget) || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!settings.bookingWebhookUrl.trim()) {
      alert('Aucun webhook de réservation n est configuré dans les paramètres du site.');
      return;
    }

    const localDisplayedTime =
      selectedDate && selectedTime
        ? convertScheduleTimeToLocal(selectedTime, selectedDate, settings.bookingTimezone)
        : '';
    
    const bookingData = {
      ...formData,
      budget: selectedBudgetOption?.label || formData.budget,
      budgetRange: formData.budget,
      currencyCode: currencyConfig.code,
      country,
      date: selectedDate,
      time: selectedTime,
      localTime: localDisplayedTime,
      timezone: settings.bookingTimezone,
      timezoneLabel: settings.bookingTimezoneLabel,
      userTimezone,
    };

    try {
      const response = await fetch(settings.bookingWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        alert('Rendez-vous réservé avec succès !');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          budget: '',
          description: ''
        });
        setSelectedDate('');
        setSelectedTime('');
      } else {
        throw new Error('Erreur lors de la réservation');
      }
    } catch (error) {
      alert('Une erreur est survenue. Veuillez réessayer.');
      console.error('Error:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <>
      <Helmet>
        <title>Réserver un Appel - {settings.brandFullName}</title>
        <meta
          name="description"
          content={`Réservez un appel avec les experts de ${settings.brandFullName} pour discuter de votre projet digital.`}
        />
      </Helmet>

      <main>
        {/* Hero Section */}
        <section className="py-12 md:py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--neon-orange)_0%,_transparent_100%)] opacity-20" />
            <motion.div
              className="absolute top-20 right-20 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-3xl md:text-6xl font-bold text-white mb-4 md:mb-6"
              >
                Réservez votre appel{' '}
                <span className="bg-gradient-to-r from-neon-orange to-[#FF4500] bg-clip-text text-transparent">
                  stratégique
                </span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-4"
              >
                30 minutes pour explorer ensemble votre projet et définir
                la meilleure stratégie pour atteindre vos objectifs.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-base md:text-lg text-gray-400 mt-4"
              >
                Les horaires sont affichés dans votre fuseau horaire ({userTimezone})
              </motion.p>
            </div>
          </div>
        </section>

        {/* Booking Form Section */}
        <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white relative">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-xl p-6 md:p-8 relative overflow-hidden"
            >
              <div className="space-y-8">
                {/* Personal Information */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-6">Vos informations</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {/* Name */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom et Prénom
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300 outline-none text-base md:text-lg"
                        required
                      />
                    </div>

                    {/* Email */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300 outline-none text-base md:text-lg"
                        required
                      />
                    </div>

                    {/* Phone */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300 outline-none text-base md:text-lg"
                        required
                      />
                    </div>

                    {/* Company */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Entreprise
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300 outline-none text-base md:text-lg"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Project Information */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-6">Votre projet</h2>
                  <div className="space-y-4 md:space-y-6">
                    {/* Project Type */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type de projet
                      </label>
                      <select
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300 outline-none text-base md:text-lg"
                        required
                      >
                        <option value="">Sélectionnez un type</option>
                        <option value="web">Développement Web</option>
                        <option value="mobile">Application Mobile</option>
                        <option value="marketing">Marketing Digital</option>
                        <option value="automation">Automatisation</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>

                    {/* Budget */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Budget estimé ({currencyConfig.code})
                      </label>
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300 outline-none text-base md:text-lg"
                        required
                      >
                        <option value="">Sélectionnez un budget</option>
                        {budgetOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Description */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description du projet
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300 outline-none text-base md:text-lg resize-none"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Schedule */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-6">Choisissez un créneau</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {/* Date */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date
                      </label>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300 outline-none text-base md:text-lg"
                        required
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    {/* Time */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Heure
                      </label>
                      <select
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300 outline-none text-base md:text-lg"
                        required
                      >
                        <option value="">Choisir une heure</option>
                        {selectedDate && scheduleTimeSlots.map((scheduleTime) => {
                          const localTime = convertScheduleTimeToLocal(
                            scheduleTime,
                            selectedDate,
                            settings.bookingTimezone
                          );
                          return (
                            <option key={scheduleTime} value={scheduleTime}>
                              {localTime} ({scheduleTime} heure de {settings.bookingTimezoneLabel})
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full md:w-auto bg-gradient-to-r from-neon-orange to-[#FF4500] text-white px-8 py-4 rounded-full font-semibold text-lg relative overflow-hidden group"
                  >
                    <span className="relative z-10">{settings.primaryCtaLabel}</span>
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: "100%" }}
                      transition={{ duration: 1 }}
                    />
                  </motion.button>
                </div>
              </div>
            </motion.form>
          </div>
        </section>
      </main>
    </>
  );
};

export default BookCall;
